import type { ApolloError } from "@apollo/client";
import type { IOption } from "@components/ui-kit";
import {
  type ClientSimple,
  type ContractOrderByColumnEnum,
  ContractsWhereColumn,
  type GetContractsQuery,
  type GetDocumentDependenciesForClientQuery,
  type GetDocumentDependenciesQuery,
  type GetOrdersQuery,
  type GetOtherDocumentsQuery,
  type OrderOrderByColumnEnum,
  OrdersWhereColumn,
  type OtherDocumentOrderByColumnEnum,
  OtherDocumentsWhereColumn,
  type SortOrder,
} from "@src/__generated__/graphql";
import {
  type ExternalLink,
  ExternalLinkType,
} from "@src/__generated__/urql-graphql";
import { commonSearchParams, type TRefetch } from "@src/utils/apolloHelpers";
import { setFilterOptions } from "@src/utils/components/filters";
import type { PaginationState } from "@src/utils/mobx/states/PaginationState";
import {
  createActiveQueryParam,
  parseActiveQueryParam,
} from "@src/utils/routing";
import { compact, flatten, map, orderBy } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import type { Query } from "nextjs-routes";
import React from "react";
import type {
  BaseMixedFilters,
  IMixedOrderBy,
  IMixedWhere,
} from "./MixedFilters";
import { ContractModel, OrderModel, OtherDocumentModel } from "./models";

export enum QuickFilters {
  Contracts = "contracts",
  Orders = "orders",
  Other = "other",
  ExternalLinks = "external_links",
}

export enum ExternalLinkColumns {
  ProjectId = "project_id",
  BrandId = "brand_id",
  ModelType = "model_type",
}

type IParts = Set<"contracts" | "orders" | "other" | "external_links">;

export class StoreDocuments {
  refetchContracts?: TRefetch<any, any>;
  refetchOrders?: TRefetch<any, any>;
  refetchOtherDocs?: TRefetch<any, any>;
  refetchExtLinks?: () => void;

  asExternalModule = false;
  showParts: IParts = new Set([
    "contracts",
    "orders",
    "other",
    "external_links",
  ]);
  hideColsInExternalModule = {
    contractors: ["relatedTo"],
    orders: [] as string[],
    otherDocuments: ["relatedTo"],
  };

  pagination: PaginationState;
  whereMixed: IMixedWhere;
  orderByMixed: IMixedOrderBy;

  @observable.ref error?: ApolloError;
  @observable loading = false;
  @observable searchTerm = "";
  @observable activeQuickFilter = QuickFilters.Contracts;
  @observable prefillFormFields: {
    projectIds?: string[];
    brandId?: string;
    clientId?: string;
    contractorId?: string;
  } = {};
  @observable.ref contracts: ContractModel[] = [];
  @observable.ref orders: OrderModel[] = [];
  @observable.ref otherDocuments: OtherDocumentModel[] = [];

  @observable.ref brands: IOption[] = [];
  @observable.ref clients: IOption[] = [];
  @observable.ref projects: (IOption & { brandId: string })[] = [];
  contractors: IOption[] = [];
  constructor(mixedFilters: BaseMixedFilters, asExternalModule = false) {
    makeObservable(this);
    this.pagination = mixedFilters.pagination;
    this.whereMixed = mixedFilters.where;
    this.orderByMixed = mixedFilters.orderBy;
    this.asExternalModule = asExternalModule;
  }

  @computed get where() {
    switch (this.activeQuickFilter) {
      case QuickFilters.Contracts:
        return this.whereMixed.contracts;
      case QuickFilters.Orders:
        return this.whereMixed.orders;
      case QuickFilters.Other:
        return this.whereMixed.otherDocuments;
      case QuickFilters.ExternalLinks:
        return this.whereMixed.extLinks;
      default:
        return this.whereMixed.contracts;
    }
  }

  @computed get orderBy() {
    switch (this.activeQuickFilter) {
      case QuickFilters.Contracts:
        return this.orderByMixed.contracts;
      case QuickFilters.Orders:
        return this.orderByMixed.orders;
      case QuickFilters.Other:
        return this.orderByMixed.otherDocuments;
      default:
        return this.orderByMixed.contracts;
    }
  }

  @computed get searchParams() {
    type TOrderByMixed =
      | ContractOrderByColumnEnum
      | OrderOrderByColumnEnum
      | OtherDocumentOrderByColumnEnum;
    type TWhereMixed =
      | ContractsWhereColumn
      | OrdersWhereColumn
      | OtherDocumentsWhereColumn
      | ExternalLinkColumns;
    return {
      ...commonSearchParams<TOrderByMixed, TWhereMixed>({
        where: this.where,
        orderBy: this.orderBy,
        pagination: this.pagination,
      }),
      ...createActiveQueryParam(this.activeQuickFilter),
    };
  }

  @action
  setShowPart(parts: IParts) {
    this.showParts = parts;
  }

  @action
  setPrefillFormFields(
    currentIds:
      | { brandId?: string }
      | { clientId?: string }
      | { contractorId?: string }
      | { projectId?: string } = {},
  ) {
    this.prefillFormFields = {
      ...currentIds,
      ...("projectId" in currentIds
        ? { projectIds: compact([currentIds?.projectId]) }
        : {}),
    } as StoreDocuments["prefillFormFields"];
  }

  @action
  setActiveQuickFilter(quickFilter: QuickFilters) {
    this.activeQuickFilter = quickFilter;
  }

  @action
  setQuickFilterFromURL(query: Query) {
    const active = parseActiveQueryParam(query);
    switch (active) {
      case QuickFilters.Orders:
        this.setActiveQuickFilter(QuickFilters.Orders);
        break;
      case QuickFilters.Other:
        this.setActiveQuickFilter(QuickFilters.Other);
        break;
      case QuickFilters.Contracts:
      default:
        this.setActiveQuickFilter(QuickFilters.Contracts);
        break;
    }
  }

  @action
  fromGetDocumentDependenciesForClientQuery(
    data: GetDocumentDependenciesForClientQuery,
  ) {
    const { client, ...restData } = data;
    this.fromGetDocumentDependenciesQuery({
      clientsSimpleMap: compact([client]) as ClientSimple[],
      ...restData,
    });
  }

  @action
  fromGetDocumentDependenciesQuery(data: GetDocumentDependenciesQuery) {
    this.brands = orderBy(
      flatten(
        map(data.clientsSimpleMap, (i) =>
          i.brands.map((brand) => {
            return {
              label: brand.name,
              value: brand.id,
            };
          }),
        ),
      ),
      ["label"],
      ["asc"],
    );

    this.clients = orderBy(
      map(data.clientsSimpleMap, (i) => ({
        value: i.id,
        label: i.name,
      })),
      ["label"],
      ["asc"],
    );

    this.projects = orderBy(
      map(data.projectsSimpleMap, (project) => ({
        value: project.id,
        label: `${project.code} ${project.title}`,
        brandId: project.brand.id,
      })),
      ["label"],
      ["asc"],
    );

    setFilterOptions(
      this.whereMixed.orders,
      OrdersWhereColumn.BrandId,
      this.brands,
    );
    setFilterOptions(
      this.whereMixed.orders,
      OrdersWhereColumn.ProjectId,
      this.projects,
    );

    setFilterOptions(
      this.whereMixed.otherDocuments,
      OtherDocumentsWhereColumn.BrandId,
      this.brands,
    );
    setFilterOptions(
      this.whereMixed.otherDocuments,
      OtherDocumentsWhereColumn.ClientId,
      this.clients,
    );
    setFilterOptions(
      this.whereMixed.otherDocuments,
      OtherDocumentsWhereColumn.ProjectId,
      this.projects,
    );

    setFilterOptions(
      this.whereMixed.contracts,
      ContractsWhereColumn.BrandId,
      this.brands,
    );
    setFilterOptions(
      this.whereMixed.contracts,
      ContractsWhereColumn.ClientId,
      this.clients,
    );
    setFilterOptions(
      this.whereMixed.contracts,
      ContractsWhereColumn.ProjectId,
      this.projects,
    );
  }

  @computed get extLinkType() {
    const pathname = window.location.pathname;
    return pathname.includes("/project")
      ? ExternalLinkType.Project
      : pathname.includes("/brand")
        ? ExternalLinkType.Brand
        : pathname.includes("/documents")
          ? "all"
          : undefined;
  }

  @computed get extLinkModelId(): ExternalLink["model_id"] | undefined {
    if (!Router.query.id) return undefined;
    if (typeof Router.query.id !== "string" || Array.isArray(Router.query.id))
      return undefined;
    return Router.query.id;
  }

  fromGetOrdersQuery(data: GetOrdersQuery) {
    this.orders = OrderModel.fromGetOrdersQuery(data.orders);
    if (data.orders) {
      this.pagination.setFromPaginatorInfo(data.orders.paginatorInfo);
    }
  }

  fromGetOtherDocumentsQuery(data: GetOtherDocumentsQuery) {
    this.otherDocuments = OtherDocumentModel.fromGetOtherDocumentsQuery(
      data.otherDocuments,
    );
    if (data.otherDocuments) {
      this.pagination.setFromPaginatorInfo(data.otherDocuments.paginatorInfo);
    }
  }

  fromGetContractsQuery(data: GetContractsQuery) {
    this.contracts = ContractModel.fromGetContractsQuery(data.contracts);
    if (data.contracts) {
      this.pagination.setFromPaginatorInfo(data.contracts.paginatorInfo);
    }
  }

  resetTableState() {
    this.searchTerm = "";
    this.pagination.setPage(1);
  }

  @action.bound
  handleContractsOrderByChange(clause: {
    column: ContractOrderByColumnEnum;
    order: SortOrder | undefined;
  }): void {
    if (!clause.order) {
      this.orderByMixed.contracts.setClauses([]);
      return;
    }
    this.orderByMixed.contracts.setClauses([
      { column: clause.column, order: clause.order },
    ]);
  }

  @action.bound
  handleOrdersOrderByChange(clause: {
    column: OrderOrderByColumnEnum;
    order: SortOrder | undefined;
  }): void {
    if (!clause.order) {
      this.orderByMixed.orders.setClauses([]);
      return;
    }
    this.orderByMixed.orders.setClauses([
      { column: clause.column, order: clause.order },
    ]);
  }

  @action.bound
  handleOtherDocsOrderByChange(clause: {
    column: OtherDocumentOrderByColumnEnum;
    order: SortOrder | undefined;
  }): void {
    if (!clause.order) {
      this.orderByMixed.otherDocuments.setClauses([]);
      return;
    }
    this.orderByMixed.otherDocuments.setClauses([
      { column: clause.column, order: clause.order },
    ]);
  }
}

export const DocumentsCtx = React.createContext<StoreDocuments>(
  {} as StoreDocuments,
);
